import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FaEnvelopeOpenText, FaEnvelope, FaCheck, FaClock } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import API_URL from '../../config/api';

// دالة لعرض توست نجاح
const toastSuccess = (message) => {
  toast.success(message, {
    position: 'top-right',
    style: {
      background: '#fff',
      color: '#000',
      border: '1px solid #e5e5e5',
      padding: '16px',
      borderRadius: '0px',
      fontSize: '14px',
      fontWeight: 'normal',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    iconTheme: {
      primary: '#000',
      secondary: '#fff',
    },
  });
};

// دالة لعرض توست خطأ
const toastError = (message) => {
  toast.error(message, {
    position: 'top-right',
    style: {
      background: '#fff',
      color: '#dc2626',
      border: '1px solid #e5e5e5',
      padding: '16px',
      borderRadius: '0px',
      fontSize: '14px',
      fontWeight: 'normal',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    iconTheme: {
      primary: '#dc2626',
      secondary: '#fff',
    },
  });
};

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('forgot-password');

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required')
  });

  const requestPasswordReset = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send reset email');
      }

      return result;
    } catch (error) {
      throw new Error(error.message || 'Failed to send reset email. Please try again.');
    }
  };

  const resendPasswordReset = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to resend email');
      }

      return result;
    } catch (error) {
      throw new Error(error.message || 'Failed to resend email. Please try again.');
    }
  };

  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const result = await requestPasswordReset(values.email);
        
        // عرض رسالة النجاح من الباك إند في التوستر
        if (result.success) {
          toastSuccess(result.message || 'Password reset link sent successfully');
        }
        
        setCurrentView('email-sent');
      } catch (error) {
        // عرض رسالة الخطأ في التوستر
        toastError(error.message);
        formik.setFieldError('email', error.message);
      } finally {
        setLoading(false);
      }
    }
  });

  const handleResendEmail = async () => {
    setLoading(true);
    try {
      const result = await resendPasswordReset(formik.values.email);
      
      // عرض رسالة النجاح من الباك إند في التوستر
      if (result.success) {
        toastSuccess(result.message || 'Password reset link resent successfully');
      }
      
      setCurrentView('resend-confirmation');
    } catch (error) {
      // عرض رسالة الخطأ في التوستر
      toastError(error.message);
      formik.setFieldError('email', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTryDifferentEmail = () => {
    setCurrentView('forgot-password');
    formik.resetForm();
  };

  const renderView = () => {
    if (currentView === 'forgot-password') {
      return (
        <div className="flex min-h-screen">
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#fff',
                color: '#000',
                border: '1px solid #e5e5e5',
                padding: '16px',
                borderRadius: '0px',
                fontSize: '14px',
                fontWeight: 'normal',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              },
              success: {
                iconTheme: {
                  primary: '#000',
                  secondary: '#fff',
                },
              },
              error: {
                style: {
                  background: '#fff',
                  color: '#dc2626',
                },
                iconTheme: {
                  primary: '#dc2626',
                  secondary: '#fff',
                },
              },
            }}
          />
          
          <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
            <div className="w-full max-w-[440px]">
              <div className="mb-12">
                <div className="mb-12">
                  <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
                    Reset Password
                  </h2>
                  <p className="text-sm text-gray-600 font-normal tracking-wide">
                    Enter your email to receive reset instructions
                  </p>
                </div>
                
                <form className="space-y-8" onSubmit={formik.handleSubmit}>
                  <div>
                    <label htmlFor="email" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                      Email Address
                    </label>
                    <input 
                      type="email" 
                      id="email"
                      name="email"
                      required 
                      placeholder="your@email.com"
                      className={`w-full px-4 py-3 border ${formik.touched.email && formik.errors.email ? 'border-red-300' : 'border-gray-200'} focus:border-gray-300 focus:outline-none transition-colors text-sm font-normal placeholder:text-gray-400`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="text-red-600 text-xs mt-1">{formik.errors.email}</div>
                    )}
                  </div>
                  
                  <div className="pt-8">
                    <button 
                      type="submit"
                      disabled={loading} 
                      className="w-full bg-black text-white py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </div>
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>
                  </div>
                </form>
                
                <div className="mt-12 text-center">
                  <p className="text-sm text-gray-600 font-normal tracking-wide">
                    Remember your password?
                    <Link to="/signin" className="text-gallery-dark hover:text-gray-600 transition-colors font-normal ml-1 underline">
                      Sign in
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (currentView === 'email-sent') {
      return (
        <div className="flex min-h-screen">
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#fff',
                color: '#000',
                border: '1px solid #e5e5e5',
                padding: '16px',
                borderRadius: '0px',
                fontSize: '14px',
                fontWeight: 'normal',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              },
              success: {
                iconTheme: {
                  primary: '#000',
                  secondary: '#fff',
                },
              },
              error: {
                style: {
                  background: '#fff',
                  color: '#dc2626',
                },
                iconTheme: {
                  primary: '#dc2626',
                  secondary: '#fff',
                },
              },
            }}
          />
          
          <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
            <div className="w-full max-w-[440px]">
              <div className="mb-12">
                <div className="mb-12 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-8">
                    <FaEnvelopeOpenText className="text-gallery-dark text-3xl" />
                  </div>
                  <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
                    Check Your Email
                  </h2>
                  <p className="text-sm text-gray-600 font-normal tracking-wide leading-relaxed max-w-md mx-auto">
                    If an account is associated with this email, a password reset link has been sent to <span className="text-gallery-dark font-medium">{formik.values.email}</span>
                  </p>
                </div>
                
                <div className="space-y-6">
                  <div className="p-6 border border-gray-200">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-normal">
                        1
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gallery-dark mb-2 tracking-wide">
                          Check your inbox
                        </p>
                        <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                          Look for an email from ArtRewards with reset instructions
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-normal">
                        2
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gallery-dark mb-2 tracking-wide">
                          Click the reset link
                        </p>
                        <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                          The link will direct you to a secure page to create a new password
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-normal">
                        3
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gallery-dark mb-2 tracking-wide">
                          Create new password
                        </p>
                        <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                          Choose a strong password and regain access to your account
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center pt-6">
                    <p className="text-sm text-gray-600 font-normal tracking-wide mb-4">
                      Didn't receive the email?
                    </p>
                    <button 
                      onClick={handleResendEmail}
                      disabled={loading}
                      className="text-sm text-gallery-dark hover:text-gray-600 transition-colors font-normal underline tracking-wide disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Resending...' : 'Resend email'}
                    </button>
                  </div>
                </div>
                
                <div className="mt-12 text-center">
                  <button 
                    onClick={handleTryDifferentEmail}
                    className="text-sm text-gray-600 hover:text-gallery-dark transition-colors font-normal tracking-wide underline"
                  >
                    Try a different email address
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (currentView === 'resend-confirmation') {
      return (
        <div className="flex min-h-screen">
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#fff',
                color: '#000',
                border: '1px solid #e5e5e5',
                padding: '16px',
                borderRadius: '0px',
                fontSize: '14px',
                fontWeight: 'normal',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              },
              success: {
                iconTheme: {
                  primary: '#000',
                  secondary: '#fff',
                },
              },
              error: {
                style: {
                  background: '#fff',
                  color: '#dc2626',
                },
                iconTheme: {
                  primary: '#dc2626',
                  secondary: '#fff',
                },
              },
            }}
          />
          
          <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
            <div className="w-full max-w-[440px]">
              <div className="mb-12">
                <div className="mb-12 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gallery-dark mb-8">
                    <FaCheck className="text-white text-3xl" />
                  </div>
                  <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
                    Email Resent
                  </h2>
                  <p className="text-sm text-gray-600 font-normal tracking-wide leading-relaxed max-w-md mx-auto">
                    We've sent another copy of the password reset instructions to <span className="text-gallery-dark font-medium">{formik.values.email}</span>
                  </p>
                </div>
                
                <div className="p-6 border border-gray-200 bg-gallery-gray">
                  <div className="flex items-start space-x-4">
                    <FaClock className="text-gallery-dark text-lg mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gallery-dark mb-3 tracking-wide">
                        Still Not Receiving Emails?
                      </p>
                      <ul className="text-sm text-gray-600 font-normal leading-relaxed tracking-wide space-y-2">
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Check your spam or junk folder</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Verify the email address is correct</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Add no-reply@artrewards.com to your contacts</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Contact our support team for assistance</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 space-y-4">
                  <Link 
                    to="/support" 
                    className="block w-full bg-black text-white px-8 py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase text-center hover:bg-gray-700"
                  >
                    Contact Support
                  </Link>
                  
                  <button 
                    onClick={handleTryDifferentEmail}
                    className="block w-full border border-gray-300 bg-white text-gray-700 px-8 py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase text-center hover:bg-gray-50"
                  >
                    Try Different Email
                  </button>
                  
                  <div className="pt-4 text-center">
                    <Link 
                      to="/signin" 
                      className="text-sm text-gray-600 hover:text-gallery-dark transition-colors font-normal tracking-wide underline"
                    >
                      Back to Sign In
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return renderView();
};
export default ForgotPassword;
